import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface FeedbackItem {
  id: string;
  content: any;
  submitted_at: string;
}

export const useVisitorFeedback = (terraceId: string) => {
  const [photos, setPhotos] = useState<FeedbackItem[]>([]);
  const [sunnyHours, setSunnyHours] = useState<FeedbackItem[]>([]);
  const [comments, setComments] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Fetch feedback data
  const fetchFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from('visitor_checkins')
        .select('*')
        .eq('terrace_id', terraceId)
        .eq('is_approved', true)
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      const photoData = data?.filter(item => item.feedback_type === 'photo') || [];
      const sunnyData = data?.filter(item => item.feedback_type === 'sunny_hours') || [];
      const commentData = data?.filter(item => item.feedback_type === 'comment') || [];

      setPhotos(photoData);
      setSunnyHours(sunnyData);
      setComments(commentData);
    } catch (error) {
      console.error('Error fetching visitor feedback:', error);
      toast({
        title: "Error",
        description: "Failed to load visitor feedback",
        variant: "destructive"
      });
    }
  };

  // Submit photo from URL
  const submitPhoto = async (photoUrl: string, caption?: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('visitor_checkins')
        .insert({
          terrace_id: terraceId,
          feedback_type: 'photo',
          content: {
            photo_url: photoUrl,
            caption: caption
          },
          submitted_by: 'guest'
        });

      if (error) throw error;

      toast({
        title: "Photo shared!",
        description: "Thanks for sharing your photo"
      });

      fetchFeedback(); // Refresh data
    } catch (error) {
      console.error('Error submitting photo:', error);
      toast({
        title: "Error",
        description: "Failed to share photo",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit photo from file upload
  const submitFile = async (file: File, caption?: string) => {
    setLoading(true);
    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `visitor-photos/${fileName}`;

      // Upload file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('terrace-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('terrace-photos')
        .getPublicUrl(filePath);

      // Save to visitor_checkins
      const { error } = await supabase
        .from('visitor_checkins')
        .insert({
          terrace_id: terraceId,
          feedback_type: 'photo',
          content: {
            photo_url: urlData.publicUrl,
            caption: caption
          },
          submitted_by: 'guest'
        });

      if (error) throw error;

      toast({
        title: "Photo uploaded!",
        description: "Thanks for sharing your photo"
      });

      fetchFeedback(); // Refresh data
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: "Error",
        description: "Failed to upload photo",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit sunny hours
  const submitSunnyHours = async (fromTime: string, toTime: string, date: string, notes?: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('visitor_checkins')
        .insert({
          terrace_id: terraceId,
          feedback_type: 'sunny_hours',
          content: {
            from_time: fromTime,
            to_time: toTime,
            date: date,
            notes: notes
          },
          submitted_by: 'guest'
        });

      if (error) throw error;

      toast({
        title: "Sunny hours reported!",
        description: "Thanks for the sunshine info"
      });

      fetchFeedback(); // Refresh data
    } catch (error) {
      console.error('Error submitting sunny hours:', error);
      toast({
        title: "Error",
        description: "Failed to report sunny hours",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit comment
  const submitComment = async (comment: string, category?: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('visitor_checkins')
        .insert({
          terrace_id: terraceId,
          feedback_type: 'comment',
          content: {
            comment_text: comment,
            category: category
          },
          submitted_by: 'guest'
        });

      if (error) throw error;

      toast({
        title: "Feedback submitted!",
        description: "Thanks for your input"
      });

      fetchFeedback(); // Refresh data
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        title: "Error",
        description: "Failed to submit feedback",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (terraceId) {
      fetchFeedback();
    }
  }, [terraceId]);

  return {
    photos,
    sunnyHours,
    comments,
    submitPhoto,
    submitFile,
    submitSunnyHours,
    submitComment,
    loading
  };
};